import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { COMPANY_SLUG } from '@/config/companyProfile'
import api from '@/utils/api/apiClient'

const AuthContext = createContext()

const USER_KEY = `${COMPANY_SLUG}_user`
const TOKEN_KEY = `${COMPANY_SLUG}_token`
const REFRESH_TOKEN_KEY = `${COMPANY_SLUG}_refresh_token`

export function AuthProvider ({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const storedUser = localStorage.getItem(USER_KEY)
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser))
      } catch {
        localStorage.removeItem(USER_KEY)
      }
    }
    setLoading(false)
  }, [])

  const setSession = useCallback(({ user, accessToken, refreshToken }) => {
    if (accessToken) localStorage.setItem(TOKEN_KEY, accessToken)
    if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
    if (user) {
      localStorage.setItem(USER_KEY, JSON.stringify(user))
      setUser(user)
    }
  }, [])

  const clearSession = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
    setUser(null)
  }, [])

  const login = useCallback(async (credentials) => {
    setError(null)
    const { data } = await api.post('/auth/login', credentials)
    setSession(data)
    return data.user
  }, [setSession])

  const register = useCallback(async (payload) => {
    setError(null)
    const { data } = await api.post('/auth/register', payload)
    setSession(data)
    return data.user
  }, [setSession])

  const refreshProfile = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token) return null
    try {
      const { data } = await api.get('/auth/me')
      localStorage.setItem(USER_KEY, JSON.stringify(data))
      setUser(data)
      return data
    } catch (err) {
      clearSession()
      return null
    }
  }, [clearSession])

  const updateProfile = useCallback(async (patch) => {
    setError(null)
    const { data } = await api.patch('/auth/me', patch)
    localStorage.setItem(USER_KEY, JSON.stringify(data))
    setUser(data)
    return data
  }, [])

  const logout = useCallback(() => {
    clearSession()
  }, [clearSession])

  const value = useMemo(() => ({
    user,
    loading,
    error,
    login,
    register,
    logout,
    refreshProfile,
    updateProfile
  }), [user, loading, error, login, register, logout, refreshProfile, updateProfile])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
